import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Spinner, Table, Badge, Nav, Tab, Modal,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart, PieChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import budgetService from '../../../services/budgetService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import '../../../styles/react-select.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

echarts.use([LineChart, BarChart, PieChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent, CanvasRenderer]);

const TIGO_CLIENT_ID = 28;

const getCreativeProxyUrl = (ad) => {
  if (!ad?.adKey) return null;
  const base = process.env.REACT_APP_API_URL || (
    window.location.hostname.includes('.herrerahavas.')
      ? `${window.location.protocol}//${window.location.hostname}:3001/api`
      : 'http://localhost:3001/api'
  );
  return `${base}/budget/creative-image/${ad.adKey}`;
};

const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num?.toLocaleString() || '0';
};
const formatNumberFull = (num) => (num || 0).toLocaleString('es-MX');
const formatCurrency = (num) => '$' + (num || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatPercent = (num) => (num || 0).toFixed(2) + '%';

const METRIC_OPTIONS = [
  { key: 'cost', label: 'Inversión', isCurrency: true },
  { key: 'impressions', label: 'Impresiones' },
  { key: 'clicks', label: 'Clics' },
  { key: 'linkClicks', label: 'Link Clics' },
  { key: 'leads', label: 'Leads' },
  { key: 'cpm', label: 'CPM', isCurrency: true },
  { key: 'cpc', label: 'CPC', isCurrency: true },
  { key: 'cpl', label: 'CPL', isCurrency: true },
  { key: 'ctr', label: 'CTR %', isPercent: true },
];

const TOP_AD_METRICS = [
  { key: 'impressions', label: 'Impresiones', fmt: formatNumber, color: '#2c7be5' },
  { key: 'clicks', label: 'Clicks', fmt: formatNumber, color: '#00d97e' },
  { key: 'ctr', label: 'CTR %', fmt: v => formatPercent(v), color: '#f5803e' },
  { key: 'leads', label: 'Leads', fmt: formatNumber, color: '#6b5eae' },
  { key: 'cpl', label: 'CPL', fmt: v => formatCurrency(v), color: '#e63757', sortAsc: true },
  { key: 'cost', label: 'Inversión', fmt: v => formatCurrency(v), color: '#27bcfd' },
];

const PLATFORM_BADGE_COLORS = { META: '#1877f2', GOOGLE: '#34a853', TIKTOK: '#010101' };

const PlatformBadge = ({ platform }) => {
  const p = (platform || '').toUpperCase();
  const color = PLATFORM_BADGE_COLORS[p] || '#748194';
  return <Badge bg="" style={{ backgroundColor: color, fontSize: 8, fontWeight: 500 }}>{p || '?'}</Badge>;
};

const KPICard = ({ title, value, subtitle, color = '#2c7be5', tooltip }) => (
  <Card className="h-100 border-0 shadow-sm text-center" style={{ borderTop: `3px solid ${color}` }} title={tooltip}>
    <Card.Body className="py-3">
      <p className="text-muted small mb-1 text-uppercase">{title}{tooltip && <span style={{ marginLeft: 4, opacity: 0.6 }}>ⓘ</span>}</p>
      <h3 className="mb-0 fw-bold" style={{ color }}>{value}</h3>
      {subtitle && <small className="text-muted">{subtitle}</small>}
    </Card.Body>
  </Card>
);

const SortHeader = ({ field, label, sortBy, onSort }) => (
  <th className="text-end" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort(field)}>
    {label} {sortBy?.field === field ? (sortBy.direction === 'desc' ? '▼' : '▲') : ''}
  </th>
);

const ChartExportMenu = ({ data, filename, columns }) => {
  if (!data || data.length === 0) return null;
  const doExport = () => {
    const rows = data.map(r => {
      const o = {};
      columns.forEach(c => { o[c.header] = r[c.key]; });
      return o;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, filename);
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf]), `${filename}.xlsx`);
  };
  return <Button variant="outline-secondary" size="sm" onClick={doExport} style={{ fontSize: 10, padding: '2px 8px' }}>↓ Excel</Button>;
};

// ── Evolutive Chart (same pattern as Claro) ──
const EvolutiveChart = ({ data }) => {
  const [selectedMetric, setSelectedMetric] = useState('impressions');
  const [selectedKPI, setSelectedKPI] = useState('cost');
  const metricConfig = METRIC_OPTIONS.find(m => m.key === selectedMetric) || METRIC_OPTIONS[1];
  const kpiConfig = METRIC_OPTIONS.find(m => m.key === selectedKPI) || METRIC_OPTIONS[0];
  const fmtVal = (v, cfg) => cfg.isCurrency ? '$' + formatNumber(v) : cfg.isPercent ? formatPercent(v) : formatNumber(v);

  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' },
        formatter: (params) => {
          let r = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach(p => { const c = p.seriesIndex === 0 ? metricConfig : kpiConfig; r += `${p.marker} ${p.seriesName}: <strong>${fmtVal(p.value, c)}</strong><br/>`; });
          return r;
        }},
      legend: { data: [metricConfig.label, kpiConfig.label], top: 0 },
      grid: { left: 60, right: 60, bottom: 50, top: 40 },
      xAxis: { type: 'category', data: data.map(d => { const s = String(d.dateKey); return `${s.slice(4,6)}/${s.slice(6,8)}`; }), axisLabel: { fontSize: 10, rotate: 45 } },
      yAxis: [
        { type: 'value', name: metricConfig.label, nameTextStyle: { fontSize: 10 }, axisLabel: { formatter: v => fmtVal(v, metricConfig), fontSize: 10 }, splitLine: { lineStyle: { type: 'dashed', color: '#eee' } } },
        { type: 'value', name: kpiConfig.label, nameTextStyle: { fontSize: 10 }, position: 'right', axisLabel: { formatter: v => fmtVal(v, kpiConfig), fontSize: 10 }, splitLine: { show: false } },
      ],
      series: [
        { name: metricConfig.label, type: 'bar', data: data.map(d => d[selectedMetric] || 0), itemStyle: { color: '#f5803e' }, barMaxWidth: 40,
          label: { show: data.length <= 20, position: 'top', formatter: p => fmtVal(p.value, metricConfig), fontSize: 8, color: '#333' } },
        { name: kpiConfig.label, type: 'line', yAxisIndex: 1, data: data.map(d => d[selectedKPI] || 0), lineStyle: { color: '#333', width: 2 }, itemStyle: { color: '#333' }, symbol: 'circle', symbolSize: 5,
          label: { show: data.length <= 15, position: 'top', formatter: p => fmtVal(p.value, kpiConfig), fontSize: 8, color: '#333' } },
      ],
    };
  }, [data, selectedMetric, selectedKPI, metricConfig, kpiConfig]); // eslint-disable-line

  if (!data || data.length === 0) return <div className="text-center text-muted py-4">Sin datos</div>;
  return (
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
        <h6 className="mb-0">EVOLUTIVO</h6>
        <ChartExportMenu data={data} filename="evolutivo_tigo" columns={[
          { key: 'dateKey', header: 'Fecha' }, { key: 'impressions', header: 'Impresiones' },
          { key: 'clicks', header: 'Clicks' }, { key: 'leads', header: 'Leads' },
          { key: 'cost', header: 'Inversión' }, { key: 'cpm', header: 'CPM' }, { key: 'ctr', header: 'CTR' },
        ]} />
      </Card.Header>
      <Card.Body>
        <Row className="g-0">
          <Col xs={2} className="pe-2">
            <div className="mb-2 pb-1 border-bottom"><small className="text-muted fw-bold">Parámetro Métricas</small></div>
            {METRIC_OPTIONS.map(m => (
              <button key={m.key} className="btn btn-sm w-100 mb-1 text-start"
                style={{ backgroundColor: selectedMetric === m.key ? '#f5803e' : '#f8f9fa', color: selectedMetric === m.key ? 'white' : '#333', border: 'none', fontSize: '11px', padding: '5px 8px' }}
                onClick={() => setSelectedMetric(m.key)}>{m.label}</button>
            ))}
          </Col>
          <Col xs={8} className="px-2">
            <div className="text-center mb-1"><small><span style={{ color: '#f5803e' }}>● {metricConfig.label}</span>{' '}<span style={{ color: '#333' }}>● {kpiConfig.label}</span></small></div>
            <ReactEChartsCore echarts={echarts} option={options} style={{ height: 320 }} />
          </Col>
          <Col xs={2} className="ps-2">
            <div className="mb-2 pb-1 border-bottom"><small className="text-muted fw-bold">KPI (líneas)</small></div>
            {METRIC_OPTIONS.map(m => (
              <button key={m.key} className="btn btn-sm w-100 mb-1 text-start"
                style={{ backgroundColor: selectedKPI === m.key ? '#333' : '#f8f9fa', color: selectedKPI === m.key ? 'white' : '#333', border: 'none', fontSize: '11px', padding: '5px 8px' }}
                onClick={() => setSelectedKPI(m.key)}>{m.label}</button>
            ))}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

// ── Investment By Channel ──
const InvestmentByChannel = ({ campaigns }) => {
  const channelData = useMemo(() => {
    if (!campaigns || campaigns.length === 0) return [];
    const map = {};
    campaigns.forEach(c => {
      const p = c.sourcePlatform || 'OTRO';
      if (!map[p]) map[p] = { platform: p, cost: 0, impressions: 0, clicks: 0, leads: 0 };
      map[p].cost += c.cost || 0;
      map[p].impressions += c.impressions || 0;
      map[p].clicks += c.clicks || 0;
      map[p].leads += c.leads || 0;
    });
    return Object.values(map).sort((a, b) => b.cost - a.cost);
  }, [campaigns]);

  const totalCost = channelData.reduce((s, c) => s + c.cost, 0);

  const options = useMemo(() => {
    if (channelData.length === 0) return {};
    const colors = { META: '#1877f2', GOOGLE: '#34a853', TIKTOK: '#010101', LINKEDIN: '#0077B5', OTRO: '#748194' };
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' },
        formatter: (params) => {
          let r = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach(p => { r += `${p.marker} ${p.seriesName}: <strong>$${formatNumber(p.value)}</strong><br/>`; });
          return r;
        }},
      grid: { left: 50, right: 20, bottom: 30, top: 20 },
      xAxis: { type: 'category', data: channelData.map(c => c.platform), axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value', axisLabel: { formatter: v => '$' + formatNumber(v), fontSize: 10 }, splitLine: { lineStyle: { type: 'dashed', color: '#eee' } } },
      series: [{ name: 'Inversión', type: 'bar', data: channelData.map(c => ({ value: c.cost, itemStyle: { color: colors[c.platform] || '#748194' } })), barMaxWidth: 60,
        label: { show: true, position: 'top', formatter: p => '$' + formatNumber(p.value), fontSize: 10, color: '#333' } }],
    };
  }, [channelData]);

  if (channelData.length === 0) return null;

  return (
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-white border-bottom py-2"><h6 className="mb-0">Inversión por Canal</h6></Card.Header>
      <Card.Body>
        <Row>
          <Col md={8}>
            <ReactEChartsCore echarts={echarts} option={options} style={{ height: 250 }} />
          </Col>
          <Col md={4}>
            <Table size="sm" className="mt-2" style={{ fontSize: 11 }}>
              <thead><tr><th>Canal</th><th className="text-end">Inversión</th><th className="text-end">%</th></tr></thead>
              <tbody>
                {channelData.map(c => (
                  <tr key={c.platform}>
                    <td><Badge bg="" style={{ backgroundColor: { META: '#1877f2', GOOGLE: '#34a853', TIKTOK: '#010101', LINKEDIN: '#0077B5' }[c.platform] || '#748194', fontSize: 9 }}>{c.platform}</Badge></td>
                    <td className="text-end">{formatCurrency(c.cost)}</td>
                    <td className="text-end">{totalCost > 0 ? ((c.cost / totalCost) * 100).toFixed(1) + '%' : '0%'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

// ── AI Decision Signals (persisted) ──
const AISignals = ({ kpis, campaigns, clientName, dateRange, clientId, dateFrom, dateTo, filters: appliedFilters }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const dfStr = dateFrom instanceof Date ? dateFrom.toISOString().slice(0, 10) : dateFrom;
  const dtStr = dateTo instanceof Date ? dateTo.toISOString().slice(0, 10) : dateTo;

  useEffect(() => {
    if (!clientId || !dfStr || !dtStr) { setLoadingSaved(false); return; }
    const load = async () => {
      try {
        const res = await budgetService.getSavedDashboardInsights({ clientId, dateFrom: dfStr, dateTo: dtStr });
        if (res.success && res.data) { setInsights(res.data); setLastUpdated(res.data.createdAt); }
      } catch {}
      finally { setLoadingSaved(false); }
    };
    load();
  }, [clientId, dfStr, dtStr]);

  const generateInsights = useCallback(async () => {
    if (!kpis || !kpis.impressions) return;
    setLoading(true);
    setError(null);
    try {
      const res = await budgetService.getDashboardInsights({
        kpis, campaigns: campaigns?.slice(0, 10), clientName, dateRange,
        clientId, dateFrom: dfStr, dateTo: dtStr, filters: appliedFilters,
      });
      if (res.success) { setInsights(res.data); setLastUpdated(new Date().toISOString()); }
      else setError(res.error);
    } catch (e) { setError('Error al conectar con IA'); }
    finally { setLoading(false); }
  }, [kpis, campaigns, clientName, dateRange, clientId, dfStr, dtStr, appliedFilters]);

  const signalIcons = { warning: 'exclamation-triangle', success: 'check-circle', info: 'info-circle', action: 'bolt' };
  const signalVariants = { warning: 'warning', success: 'success', info: 'primary', action: 'info' };

  const scoreColor = (s) => s >= 70 ? '#00d97e' : s >= 40 ? '#f5803e' : '#e63757';
  const scoreLabel = (s) => s >= 70 ? 'Saludable' : s >= 40 ? 'Atención' : 'Crítico';

  if (loadingSaved) return null;

  return (
    <Card className="shadow-none border h-100">
      <Card.Header className="bg-body-tertiary d-flex justify-content-between align-items-center py-2">
        <div className="d-flex align-items-center gap-2">
          <span className="fas fa-brain text-primary"></span>
          <h6 className="mb-0 fw-semibold fs--1">Señales de Decisión · IA</h6>
          {insights?.score != null && (
            <Badge pill bg="" style={{ backgroundColor: scoreColor(insights.score), fontSize: 10, padding: '4px 8px' }}>
              {insights.score}/100 · {scoreLabel(insights.score)}
            </Badge>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {lastUpdated && <small className="text-muted" style={{ fontSize: 10 }}>{new Date(lastUpdated).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}</small>}
          <Button size="sm" variant={insights ? 'falcon-default' : 'primary'} className="px-3" onClick={generateInsights} disabled={loading || !kpis?.impressions}>
            {loading ? <Spinner size="sm" animation="border" /> : <><i className={`fas fa-${insights ? 'sync-alt' : 'magic'} me-1`}></i>{insights ? 'Actualizar' : 'Analizar'}</>}
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="py-2">
        {!insights && !loading && !error && (
          <div className="text-center py-4">
            <div className="rounded-circle bg-soft-primary d-inline-flex align-items-center justify-content-center mb-2" style={{ width: 48, height: 48 }}>
              <i className="fas fa-brain text-primary fs-4"></i>
            </div>
            <p className="mb-0 text-muted fs--1">Genera un análisis inteligente de tus métricas para obtener recomendaciones accionables.</p>
          </div>
        )}
        {error && <div className="alert alert-soft-warning py-2 fs--1 mb-0"><i className="fas fa-exclamation-circle me-1"></i>{error}</div>}
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" size="sm" />
            <p className="mt-2 text-muted fs--1 mb-0">Analizando con IA...</p>
          </div>
        )}
        {insights && !loading && (
          <>
            {insights.summary && (
              <div className="bg-light rounded p-2 mb-3">
                <p className="mb-0 fs--1 text-800">{insights.summary}</p>
              </div>
            )}
            <div className="d-flex flex-column gap-2">
              {(insights.signals || []).map((signal, i) => (
                <div key={i} className={`border-start border-3 border-${signalVariants[signal.type] || 'secondary'} rounded-end bg-white p-2 shadow-sm`}>
                  <div className="d-flex align-items-start gap-2">
                    <span className={`fas fa-${signalIcons[signal.type] || 'circle'} text-${signalVariants[signal.type] || 'secondary'} mt-1`} style={{ fontSize: 13 }}></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <strong className="fs--1 text-900">{signal.title}</strong>
                        {signal.metric && <Badge bg="soft-secondary" className="text-700 fw-normal" style={{ fontSize: 9 }}>{signal.metric}</Badge>}
                      </div>
                      <p className="mb-1 fs--2 text-700 lh-sm">{signal.description}</p>
                      {signal.recommendation && (
                        <div className="fs--2 fw-medium text-primary"><i className="fas fa-arrow-right me-1" style={{ fontSize: 8 }}></i>{signal.recommendation}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

// ── Top Ads Section (same pattern as Claro) ──
const TopAdsSection = ({ ads, campaigns }) => {
  const [activeMetric, setActiveMetric] = useState('impressions');
  const [previewAd, setPreviewAd] = useState(null);
  const metricCfg = TOP_AD_METRICS.find(m => m.key === activeMetric) || TOP_AD_METRICS[0];

  const topAds = useMemo(() => {
    if (!ads || ads.length === 0) return [];
    const filtered = ads.filter(a => { const v = parseFloat(a[activeMetric]) || 0; return metricCfg.sortAsc ? v > 0 : true; });
    const sorted = [...filtered].sort((a, b) => {
      const aV = parseFloat(a[activeMetric]) || 0, bV = parseFloat(b[activeMetric]) || 0;
      return metricCfg.sortAsc ? aV - bV : bV - aV;
    });
    return sorted.slice(0, 10);
  }, [ads, activeMetric, metricCfg.sortAsc]);

  const campaignName = (ad) => campaigns?.find(c => c.campaignKey === ad.campaignKey)?.campaignName || '';
  const maxVal = topAds.length > 0 ? Math.max(...topAds.map(a => Math.abs(parseFloat(a[activeMetric]) || 0)), 1) : 1;

  if (!ads || ads.length === 0) return null;

  return (
    <>
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Top Anuncios Activos</h6>
          <ChartExportMenu data={topAds} filename="top_anuncios_tigo" columns={[
            { key: 'adName', header: 'Anuncio' }, { key: 'impressions', header: 'Impresiones' },
            { key: 'clicks', header: 'Clicks' }, { key: 'leads', header: 'Leads' }, { key: 'cost', header: 'Inversión' },
          ]} />
        </Card.Header>
        <Card.Body>
          <div className="d-flex gap-1 mb-3 flex-wrap">
            {TOP_AD_METRICS.map(m => (
              <Badge key={m.key} pill bg={activeMetric === m.key ? undefined : 'light'} text={activeMetric === m.key ? 'white' : 'dark'}
                style={{ cursor: 'pointer', fontSize: '11px', padding: '6px 12px', backgroundColor: activeMetric === m.key ? m.color : undefined }}
                onClick={() => setActiveMetric(m.key)}>{m.label}</Badge>
            ))}
          </div>
          <Table hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
            <thead className="bg-light">
              <tr><th style={{ width: 30 }}>#</th><th style={{ width: 60 }}></th><th>Anuncio</th><th>Campaña</th><th className="text-end" style={{ color: metricCfg.color, fontWeight: 'bold', width: 100 }}>{metricCfg.label}</th><th style={{ width: '30%' }}>Distribución</th></tr>
            </thead>
            <tbody>
              {topAds.map((ad, idx) => {
                const val = parseFloat(ad[activeMetric]) || 0;
                const barW = maxVal > 0 ? (Math.abs(val) / maxVal) * 100 : 0;
                const proxyUrl = getCreativeProxyUrl(ad);
                const adLabel = ad.adName?.trim() || ad.creativeTitle?.trim() || `Ad #${ad.adKey}`;
                return (
                  <tr key={ad.adKey || idx} style={{ cursor: 'pointer' }} onClick={() => setPreviewAd(ad)}>
                    <td><Badge bg={idx < 3 ? 'primary' : 'secondary'} style={{ fontSize: '10px' }}>{idx + 1}</Badge></td>
                    <td>{proxyUrl ? <img src={proxyUrl} alt="" style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb' }} onError={(e) => { e.target.onerror=null; e.target.style.display='none'; }} /> : <div style={{ width: 42, height: 42, background: '#f3f4f6', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#9ca3af' }}>🖼️</div>}</td>
                    <td><div className="fw-semibold text-truncate" style={{ maxWidth: 220 }} title={adLabel}>{adLabel}</div><PlatformBadge platform={ad.sourcePlatform} /></td>
                    <td><small className="text-muted text-truncate d-block" style={{ maxWidth: 180 }}>{campaignName(ad).slice(0, 35)}</small></td>
                    <td className="text-end fw-bold" style={{ color: metricCfg.color }}>{metricCfg.fmt(val)}</td>
                    <td><div style={{ height: 22, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}><div style={{ width: `${barW}%`, height: '100%', backgroundColor: metricCfg.color, borderRadius: 4, transition: 'width 0.3s' }} /></div></td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      {previewAd && (() => {
        const proxyFull = previewAd.adKey ? `${getCreativeProxyUrl(previewAd)}?size=full` : null;
        const hasImg = !!(previewAd.imageUrl || previewAd.thumbnailUrl || proxyFull);
        const previews = [
          { label: 'Instagram', url: previewAd.previewUrlInstagram },
          { label: 'Mobile Feed', url: previewAd.previewUrlMobileFeed },
        ].filter(p => p.url);
        return (
          <Modal show centered onHide={() => setPreviewAd(null)} size="lg">
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: 16 }}>
                {previewAd.adName || 'Anuncio'}
                {previewAd.sourcePlatform && (
                  <Badge bg={previewAd.sourcePlatform === 'META' ? 'primary' : previewAd.sourcePlatform === 'GOOGLE' ? 'success' : previewAd.sourcePlatform === 'TIKTOK' ? 'danger' : 'info'}
                    className="ms-2" style={{ fontSize: 10, verticalAlign: 'middle' }}>{previewAd.sourcePlatform}</Badge>
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-3">
                <Col md={6} className="text-center">
                  {hasImg ? (
                    <img src={proxyFull || previewAd.imageUrl || previewAd.thumbnailUrl} alt=""
                      style={{ maxWidth: '100%', maxHeight: 420, borderRadius: 8, border: '1px solid #e5e7eb' }}
                      onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center text-muted bg-light" style={{ height: 300, borderRadius: 8 }}>
                      <div style={{ fontSize: 48 }}>🖼️</div>
                      <small className="mt-2">Sin imagen disponible</small>
                    </div>
                  )}
                </Col>
                <Col md={6}>
                  <div className="mb-2"><small className="text-muted">Ad ID</small><div className="fw-semibold" style={{ fontSize: 13 }}>{previewAd.adId || previewAd.adKey}</div></div>
                  {previewAd.creativeTitle && <div className="mb-2"><small className="text-muted">Título</small><div className="fw-semibold">{previewAd.creativeTitle}</div></div>}
                  {previewAd.creativeBody && <div className="mb-2"><small className="text-muted">Cuerpo</small><div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{previewAd.creativeBody.slice(0, 600)}</div></div>}
                  {previewAd.creativeObjectType && <div className="mb-2"><small className="text-muted">Tipo</small><div><Badge bg="info">{previewAd.creativeObjectType}</Badge></div></div>}
                  {previewAd.creativeCallToAction && <div className="mb-2"><small className="text-muted">Call to Action</small><div><Badge bg="warning">{previewAd.creativeCallToAction}</Badge></div></div>}
                  {previews.length > 0 && (
                    <div className="mb-2">
                      <small className="text-muted">Vista previa en placement</small>
                      <div className="d-flex gap-2 flex-wrap mt-1">
                        {previews.map(p => <a key={p.label} href={p.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary" style={{ fontSize: 11 }}>{p.label} ↗</a>)}
                      </div>
                    </div>
                  )}
                  <hr />
                  <div className="row g-2" style={{ fontSize: 12 }}>
                    <div className="col-6"><strong>Impresiones:</strong> {formatNumberFull(previewAd.impressions || 0)}</div>
                    <div className="col-6"><strong>Clicks:</strong> {formatNumberFull(previewAd.clicks || 0)}</div>
                    <div className="col-6"><strong>Inversión:</strong> {formatCurrency(previewAd.cost || 0)}</div>
                    <div className="col-6"><strong>Leads:</strong> {formatNumberFull(previewAd.leads || 0)}</div>
                    <div className="col-6"><strong>CTR:</strong> {formatPercent(previewAd.ctr || 0)}</div>
                    <div className="col-6"><strong>Reach:</strong> {formatNumberFull(previewAd.reach || 0)}</div>
                  </div>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer><Button variant="secondary" onClick={() => setPreviewAd(null)}>Cerrar</Button></Modal.Footer>
          </Modal>
        );
      })()}
    </>
  );
};

// ── Trend Chart ──
const TrendChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    const labels = data.map(d => { const s = String(d.dateKey); return `${s.slice(4,6)}/${s.slice(6,8)}`; });
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['Impresiones', 'Clicks', 'Gasto'], top: 0 },
      grid: { left: 50, right: 50, bottom: 40, top: 40 },
      xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 10, rotate: 45 } },
      yAxis: [
        { type: 'value', axisLabel: { formatter: v => formatNumber(v), fontSize: 10 } },
        { type: 'value', position: 'right', axisLabel: { formatter: v => '$' + formatNumber(v), fontSize: 10 } },
      ],
      series: [
        { name: 'Impresiones', type: 'line', data: data.map(d => d.impressions || 0), lineStyle: { color: '#2c7be5' }, itemStyle: { color: '#2c7be5' } },
        { name: 'Clicks', type: 'line', data: data.map(d => d.clicks || 0), lineStyle: { color: '#00d97e' }, itemStyle: { color: '#00d97e' } },
        { name: 'Gasto', type: 'line', yAxisIndex: 1, data: data.map(d => d.cost || 0), lineStyle: { color: '#e63757' }, itemStyle: { color: '#e63757' } },
      ],
    };
  }, [data]);
  if (!data || data.length === 0) return <div className="text-center text-muted py-4">Sin datos</div>;
  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 260 }} />;
};

// ── Age Chart (with percentage + CPL) ──
const AgeChart = ({ data }) => {
  const totalImpressions = useMemo(() => (data || []).reduce((s, a) => s + (a.impressions || 0), 0), [data]);
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    return {
      tooltip: { trigger: 'item', formatter: (p) => {
        const row = data[p.dataIndex];
        const cpl = row && row.leads > 0 ? (row.cost / row.leads).toFixed(2) : 'N/A';
        return `<strong>${p.name}</strong><br/>Impresiones: ${formatNumber(p.value)} (${p.percent}%)<br/>Leads: ${formatNumber(row?.leads || 0)}<br/>CPL: $${cpl}`;
      }},
      series: [{ type: 'pie', radius: ['35%', '65%'],
        data: data.map(a => ({ name: a.ageRange || 'N/A', value: a.impressions || 0 })),
        label: { formatter: (p) => `${p.name}\n${p.percent}%`, fontSize: 10 },
        emphasis: { label: { show: true, fontWeight: 'bold' } },
      }],
    };
  }, [data]);
  if (!data || data.length === 0) return <div className="text-center text-muted py-4">Sin datos de edad</div>;
  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 250 }} />;
};

// ── Platform Chart ──
const PlatformChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 100, right: 30, top: 10, bottom: 20 },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: data.map(p => p.platform) },
      series: [
        { type: 'bar', data: data.map(p => p.impressions || 0), name: 'Impresiones', itemStyle: { color: '#2c7be5' } },
        { type: 'bar', data: data.map(p => p.clicks || 0), name: 'Clicks', itemStyle: { color: '#00d97e' } },
      ],
    };
  }, [data]);
  if (!data || data.length === 0) return <div className="text-center text-muted py-4">Sin datos</div>;
  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 200 }} />;
};

// ── Location Chart ──
const LocationChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    const top8 = [...data].sort((a, b) => (b.impressions || 0) - (a.impressions || 0)).slice(0, 8);
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 120, right: 20, top: 10, bottom: 20 },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: top8.map(l => l.region || 'Desc.'), inverse: true },
      series: [{ type: 'bar', data: top8.map(l => l.impressions || 0), itemStyle: { color: '#2c7be5' } }],
    };
  }, [data]);
  if (!data || data.length === 0) return <div className="text-center text-muted py-4">Sin datos</div>;
  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 250 }} />;
};

// ── Campaign Tree Table (same pattern as Claro) ──
const CampaignTreeTable = ({ campaigns, adGroups, ads, sortBy, onSort, filters = {}, onFilterChange }) => {
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [expandedAdGroups, setExpandedAdGroups] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const f = filters || {};

  const filtered = useMemo(() => {
    if (!campaigns?.length) return [];
    return campaigns.filter(c => {
      if (f.campaignName && !(c.campaignName || '').toLowerCase().includes(f.campaignName.toLowerCase())) return false;
      if (f.accountName && !(c.accountName || '').toLowerCase().includes(f.accountName.toLowerCase())) return false;
      if (f.platform && (c.sourcePlatform || c.platform || '').toLowerCase() !== f.platform.toLowerCase()) return false;
      return true;
    });
  }, [campaigns, f.campaignName, f.accountName, f.platform]);

  const agByCampaign = useMemo(() => {
    const map = {};
    (adGroups || []).forEach(ag => { if (!map[ag.campaignKey]) map[ag.campaignKey] = []; map[ag.campaignKey].push(ag); });
    Object.values(map).forEach(arr => arr.sort((a, b) => (b.cost || 0) - (a.cost || 0)));
    return map;
  }, [adGroups]);

  const adsByAdGroup = useMemo(() => {
    const map = {};
    (ads || []).forEach(ad => { if (!map[ad.adGroupKey]) map[ad.adGroupKey] = []; map[ad.adGroupKey].push(ad); });
    Object.values(map).forEach(arr => arr.sort((a, b) => (b.cost || 0) - (a.cost || 0)));
    return map;
  }, [ads]);

  const toggleCampaign = (key) => setExpandedCampaigns(p => ({ ...p, [key]: !p[key] }));
  const toggleAdGroup = (key) => setExpandedAdGroups(p => ({ ...p, [key]: !p[key] }));
  const toggleAll = () => {
    const next = !expandAll; setExpandAll(next);
    if (next) {
      const ec = {}, ea = {};
      filtered.forEach(c => { ec[c.campaignKey] = true; (agByCampaign[c.campaignKey] || []).forEach(ag => { ea[ag.adGroupKey] = true; }); });
      setExpandedCampaigns(ec); setExpandedAdGroups(ea);
    } else { setExpandedCampaigns({}); setExpandedAdGroups({}); }
  };

  const totalCamps = filtered.length;
  const totalAg = filtered.reduce((s, c) => s + (agByCampaign[c.campaignKey]?.length || 0), 0);
  const totalAdsCount = filtered.reduce((s, c) => s + (agByCampaign[c.campaignKey] || []).reduce((s2, ag) => s2 + (adsByAdGroup[ag.adGroupKey]?.length || 0), 0), 0);

  const metricCell = (val, fmt = formatNumber) => <td className="text-end">{val > 0 ? fmt(val) : <span className="text-muted">—</span>}</td>;
  const ctrBadge = (val) => <td className="text-end"><Badge bg={val > 1 ? 'success' : val > 0.5 ? 'warning' : 'secondary'}>{formatPercent(val)}</Badge></td>;

  if (filtered.length === 0) return <div className="text-center py-5 text-muted">Sin campañas para mostrar</div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-bottom" style={{ fontSize: 12 }}>
        <div><span className="fw-semibold">{totalCamps}</span> campañas{totalAg > 0 && <> · <span className="fw-semibold">{totalAg}</span> ad groups</>}{totalAdsCount > 0 && <> · <span className="fw-semibold">{totalAdsCount}</span> anuncios</>}</div>
        <Button variant="outline-secondary" size="sm" onClick={toggleAll} style={{ fontSize: 11, padding: '2px 10px' }}>{expandAll ? '⊟ Colapsar todo' : '⊞ Expandir todo'}</Button>
      </div>
      <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
        <Table hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
          <thead className="bg-light sticky-top">
            <tr>
              <th style={{ minWidth: '300px' }}>Campaña / Ad Group / Anuncio</th>
              <th className="text-center" style={{ width: 70 }}>Plataforma</th>
              <SortHeader field="impressions" label="Impresiones" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="reach" label="Alcance" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="clicks" label="Clics" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="ctr" label="CTR" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="cpm" label="CPM" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="cpc" label="CPC" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="leads" label="Leads" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="cpl" label="CPL" sortBy={sortBy} onSort={onSort} />
              <SortHeader field="cost" label="Inversión" sortBy={sortBy} onSort={onSort} />
            </tr>
            <tr className="bg-white border-bottom">
              <th style={{ padding: '4px 6px' }}><div className="d-flex gap-1"><Form.Control size="sm" placeholder="Campaña..." value={f.campaignName || ''} onChange={e => onFilterChange?.({ ...f, campaignName: e.target.value })} style={{ fontSize: '10px', flex: 1 }} /><Form.Control size="sm" placeholder="Cuenta..." value={f.accountName || ''} onChange={e => onFilterChange?.({ ...f, accountName: e.target.value })} style={{ fontSize: '10px', flex: 1 }} /></div></th>
              <th style={{ padding: '4px 6px' }}><Form.Select size="sm" value={f.platform || ''} onChange={e => onFilterChange?.({ ...f, platform: e.target.value })} style={{ fontSize: '10px' }}><option value="">Todas</option><option value="META">Meta</option><option value="GOOGLE">Google</option><option value="TIKTOK">TikTok</option></Form.Select></th>
              <th colSpan={9} className="text-muted small" style={{ padding: '4px 8px' }}>{filtered.length} resultado(s)</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const cKey = c.campaignKey, isExpC = expandedCampaigns[cKey], campAgs = agByCampaign[cKey] || [];
              const sp = c.sourcePlatform || c.platform || 'OTRO';
              return (
                <React.Fragment key={cKey}>
                  <tr style={{ backgroundColor: '#f8f9fc', cursor: campAgs.length > 0 ? 'pointer' : 'default' }} onClick={() => campAgs.length > 0 && toggleCampaign(cKey)}>
                    <td className="fw-semibold" style={{ paddingLeft: 8 }}>
                      <span style={{ display: 'inline-block', width: 18, textAlign: 'center', fontSize: 11, color: '#6b7280' }}>{campAgs.length > 0 ? (isExpC ? '▼' : '▶') : '●'}</span>
                      <span className="text-truncate d-inline-block" style={{ maxWidth: 220, verticalAlign: 'middle' }} title={c.campaignName}>{c.campaignName}</span>
                      <small className="text-muted ms-1 d-none d-lg-inline" style={{ fontSize: 10 }}>{c.accountName}</small>
                    </td>
                    <td className="text-center"><Badge bg={sp === 'META' ? 'primary' : sp === 'GOOGLE' ? 'success' : sp === 'TIKTOK' ? 'danger' : 'secondary'} style={{ fontSize: '9px' }}>{sp === 'META' ? 'Meta' : sp === 'GOOGLE' ? 'Google' : sp === 'TIKTOK' ? 'TikTok' : sp}</Badge></td>
                    {metricCell(c.impressions)}{metricCell(c.reach)}{metricCell(c.clicks)}{ctrBadge(c.ctr)}{metricCell(c.cpm, formatCurrency)}{metricCell(c.cpc, formatCurrency)}{metricCell(c.leads)}{metricCell(c.leads > 0 ? c.cost / c.leads : 0, formatCurrency)}
                    <td className="text-end fw-semibold text-primary">{formatCurrency(c.cost)}</td>
                  </tr>
                  {isExpC && campAgs.map(ag => {
                    const agKey = ag.adGroupKey, isExpAg = expandedAdGroups[agKey], agAds = adsByAdGroup[agKey] || [];
                    return (
                      <React.Fragment key={agKey}>
                        <tr style={{ backgroundColor: '#f0f4ff', cursor: agAds.length > 0 ? 'pointer' : 'default' }} onClick={e => { e.stopPropagation(); agAds.length > 0 && toggleAdGroup(agKey); }}>
                          <td style={{ paddingLeft: 32 }}>
                            <span style={{ display: 'inline-block', width: 18, textAlign: 'center', fontSize: 10, color: '#6b7280' }}>{agAds.length > 0 ? (isExpAg ? '▼' : '▶') : '○'}</span>
                            <span className="text-truncate d-inline-block" style={{ maxWidth: 200, verticalAlign: 'middle', fontWeight: 500 }} title={ag.adGroupName}>{ag.adGroupName}</span>
                            <small className="text-muted ms-1" style={{ fontSize: 10 }}>[{agAds.length} ads]</small>
                          </td>
                          <td></td>{metricCell(ag.impressions)}{metricCell(ag.reach)}{metricCell(ag.clicks)}{ctrBadge(ag.ctr)}{metricCell(ag.cpm, formatCurrency)}{metricCell(ag.cpc, formatCurrency)}{metricCell(ag.leads)}{metricCell(ag.leads > 0 ? ag.cost / ag.leads : 0, formatCurrency)}
                          <td className="text-end">{formatCurrency(ag.cost)}</td>
                        </tr>
                        {isExpAg && agAds.map(ad => (
                          <tr key={ad.adKey} style={{ backgroundColor: '#fafbff' }}>
                            <td style={{ paddingLeft: 56 }}>
                              <span className="text-truncate d-inline-block" style={{ maxWidth: 180, verticalAlign: 'middle', fontSize: 11 }} title={ad.adName || ad.creativeTitle}>{ad.adName?.trim() || ad.creativeTitle?.trim() || `Ad #${ad.adKey}`}</span>
                            </td>
                            <td></td>{metricCell(ad.impressions)}{metricCell(ad.reach)}{metricCell(ad.clicks)}{ctrBadge(ad.ctr)}{metricCell(ad.cpm, formatCurrency)}{metricCell(ad.cpc, formatCurrency)}{metricCell(ad.leads)}{metricCell(ad.leads > 0 ? ad.cost / ad.leads : 0, formatCurrency)}
                            <td className="text-end">{formatCurrency(ad.cost)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="bg-light fw-bold" style={{ position: 'sticky', bottom: 0 }}>
            <tr>
              <td>Total ({filtered.length} campañas)</td>
              <td></td>
              <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + (c.impressions || 0), 0))}</td>
              <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + (c.reach || 0), 0))}</td>
              <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + (c.clicks || 0), 0))}</td>
              <td className="text-end">{(() => { const ti = filtered.reduce((s, c) => s + (c.impressions || 0), 0); const tc = filtered.reduce((s, c) => s + (c.clicks || 0), 0); return <Badge bg={ti > 0 && (tc/ti*100) > 1 ? 'success' : 'warning'}>{formatPercent(ti > 0 ? tc/ti*100 : 0)}</Badge>; })()}</td>
              <td className="text-end">{(() => { const ti = filtered.reduce((s, c) => s + (c.impressions || 0), 0); const tco = filtered.reduce((s, c) => s + (c.cost || 0), 0); return formatCurrency(ti > 0 ? tco / (ti / 1000) : 0); })()}</td>
              <td className="text-end">{(() => { const tc = filtered.reduce((s, c) => s + (c.clicks || 0), 0); const tco = filtered.reduce((s, c) => s + (c.cost || 0), 0); return tc > 0 ? formatCurrency(tco / tc) : '—'; })()}</td>
              <td className="text-end">{formatNumber(filtered.reduce((s, c) => s + (c.leads || 0), 0))}</td>
              <td className="text-end">{(() => { const tl = filtered.reduce((s, c) => s + (c.leads || 0), 0); const tco = filtered.reduce((s, c) => s + (c.cost || 0), 0); return tl > 0 ? formatCurrency(tco / tl) : '—'; })()}</td>
              <td className="text-end text-primary">{formatCurrency(filtered.reduce((s, c) => s + (c.cost || 0), 0))}</td>
            </tr>
          </tfoot>
        </Table>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
// ██ MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const TigoDigitalDashboard = ({ clientId }) => {
  const effectiveClientId = clientId || TIGO_CLIENT_ID;

  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d; });
  const [dateTo, setDateTo] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Filters
  const [selectedPlatform, setSelectedPlatform] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState([]);

  // Campaign tree table
  const [sortBy, setSortBy] = useState({ field: 'cost', direction: 'desc' });
  const [filterCampaigns, setFilterCampaigns] = useState({});

  // Demographics sorting
  const [sortByDemographics, setSortByDemographics] = useState({ field: 'impressions', direction: 'desc' });
  const [filterDemographics, setFilterDemographics] = useState({});

  // Platforms sorting
  const [sortByPlatforms, setSortByPlatforms] = useState({ field: 'impressions', direction: 'desc' });
  const [filterPlatforms, setFilterPlatforms] = useState({});

  // Geography
  const [geoMetric, setGeoMetric] = useState('impressions');
  const [sortByGeo, setSortByGeo] = useState({ field: 'impressions', direction: 'desc' });
  const [filterGeo, setFilterGeo] = useState({});

  const handleSort = (field) => setSortBy(p => ({ field, direction: p.field === field && p.direction === 'desc' ? 'asc' : 'desc' }));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        clientId: effectiveClientId,
        dateFrom: dateFrom.toISOString().slice(0, 10),
        dateTo: dateTo.toISOString().slice(0, 10),
      };
      if (selectedPlatform.length > 0) filters.platform = selectedPlatform.map(p => p.value).join(',');
      if (selectedAccount.length > 0) filters.accountId = selectedAccount.map(a => a.value).join(',');
      if (selectedCampaign.length > 0) filters.campaignId = selectedCampaign.map(c => c.value).join(',');
      if (selectedProduct.length > 0) filters.productType = selectedProduct.map(p => p.value).join(',');
      const res = await budgetService.getCampaignDashboardMetrics(filters);
      if (res.success) setData(res.data);
      else toast.error(res.error || 'Error al cargar datos');
    } catch (e) {
      console.error('Error loading Tigo dashboard:', e);
      toast.error('Error al conectar con el servidor');
    } finally { setLoading(false); }
  }, [effectiveClientId, dateFrom, dateTo, selectedPlatform, selectedAccount, selectedCampaign, selectedProduct]);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const kpis = data?.kpis || {};

  const sortedCampaigns = useMemo(() => {
    if (!data?.campaigns) return [];
    return [...data.campaigns].sort((a, b) => {
      const aV = a[sortBy.field] ?? 0, bV = b[sortBy.field] ?? 0;
      return sortBy.direction === 'asc' ? aV - bV : bV - aV;
    });
  }, [data?.campaigns, sortBy]);

  const accountOptions = useMemo(() => {
    if (!data?.campaigns) return [];
    const map = {};
    data.campaigns.forEach(c => { if (c.accountName) map[c.accountName] = { value: c.accountKey || c.accountName, label: c.accountName }; });
    return Object.values(map);
  }, [data?.campaigns]);

  const campaignOptions = useMemo(() => {
    if (!data?.campaigns) return [];
    const map = {};
    data.campaigns.forEach(c => { if (c.campaignName) map[c.campaignKey] = { value: c.campaignKey, label: c.campaignName }; });
    return Object.values(map).sort((a, b) => a.label.localeCompare(b.label));
  }, [data?.campaigns]);

  const productOptions = [
    { value: 'PREPAGO', label: 'Prepago' },
    { value: 'POSPAGO', label: 'Pospago' },
    { value: 'MARCA', label: 'Marca' },
    { value: 'PORTABILIDAD', label: 'Portabilidad' },
    { value: 'TERMINALES', label: 'Terminales' },
  ];

  const platformOptions = [
    { value: 'Meta', label: 'Meta Ads' },
    { value: 'Google', label: 'Google Ads' },
    { value: 'TikTok', label: 'TikTok Ads' },
  ];

  const quickRange = (days) => {
    const to = new Date();
    const from = new Date(); from.setDate(from.getDate() - days);
    setDateFrom(from); setDateTo(to);
  };

  // GA Static Data (mockup)
  const GA = {
    sessions: '142,380', duration: '2m 18s', pagesPerSession: '3.2',
    bounceRate: '62%', newUsers: '78%', convRate: '0.17%',
    costPerSession: '$0.086', purchases: 148, portability: 94,
    costPerPurchase: '$82.48', costPerPortability: '$129.86',
  };

  return (
    <Container fluid className="py-3">
      {/* Header + Filtros */}
      <Card className="shadow-sm border-0 mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-center g-2">
            <Col xs="auto"><h5 className="mb-0 fw-bold" style={{ color: '#1B2A4A' }}>Dashboard Campañas Digitales · Tigo</h5></Col>
            <Col></Col>
            <Col xs="auto" className="d-flex align-items-center gap-2">
              <small className="text-muted">Inicio</small>
              <DatePicker selected={dateFrom} onChange={setDateFrom} dateFormat="dd/MM/yyyy" className="form-control form-control-sm" style={{ width: 110 }} />
              <small className="text-muted">Fin</small>
              <DatePicker selected={dateTo} onChange={setDateTo} dateFormat="dd/MM/yyyy" className="form-control form-control-sm" style={{ width: 110 }} />
              <Button size="sm" variant="outline-secondary" onClick={() => quickRange(7)}>7d</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => quickRange(30)}>30d</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => quickRange(90)}>90d</Button>
              <Button size="sm" variant="primary" onClick={fetchData}>Buscar</Button>
            </Col>
          </Row>
          <Row className="mt-2 g-2 align-items-end">
            <Col md={2}>
              <Form.Label className="mb-0 small text-uppercase text-muted">Plataforma</Form.Label>
              <Select isMulti options={platformOptions} value={selectedPlatform} onChange={setSelectedPlatform} placeholder="Todas" classNamePrefix="react-select" isClearable />
            </Col>
            <Col md={3}>
              <Form.Label className="mb-0 small text-uppercase text-muted">Cuenta</Form.Label>
              <Select isMulti options={accountOptions} value={selectedAccount} onChange={setSelectedAccount} placeholder="Todas" classNamePrefix="react-select" isClearable />
            </Col>
            <Col md={3}>
              <Form.Label className="mb-0 small text-uppercase text-muted">Campaña</Form.Label>
              <Select isMulti options={campaignOptions} value={selectedCampaign} onChange={setSelectedCampaign} placeholder="Todas" classNamePrefix="react-select" isClearable />
            </Col>
            <Col md={2}>
              <Form.Label className="mb-0 small text-uppercase text-muted">Producto/Vertical</Form.Label>
              <Select isMulti options={productOptions} value={selectedProduct} onChange={setSelectedProduct} placeholder="Todos" classNamePrefix="react-select" isClearable />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading && <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>}

      {!loading && (
        <>
          {/* KPIs agrupados por sección */}
          <Row className="g-3 mb-3">
            {/* Alcance */}
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #27bcfd' }}>
                <Card.Header className="bg-light py-1 px-3 border-0"><small className="fw-bold text-uppercase" style={{ color: '#27bcfd' }}>Alcance</small></Card.Header>
                <Card.Body className="py-2 px-3">
                  <div className="mb-2"><small className="text-muted">Impresiones</small><div className="fw-bold">{formatNumberFull(kpis.impressions || 0)}</div></div>
                  <div className="mb-2"><small className="text-muted">Alcance</small><div className="fw-bold">{formatNumberFull(kpis.reach || 0)}</div></div>
                  <div><small className="text-muted">Frecuencia</small><div className="fw-bold">{(kpis.frequency || 0).toFixed(2)}</div></div>
                </Card.Body>
              </Card>
            </Col>
            {/* Video */}
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #00d97e' }}>
                <Card.Header className="bg-light py-1 px-3 border-0"><small className="fw-bold text-uppercase" style={{ color: '#00d97e' }}>Interacción</small></Card.Header>
                <Card.Body className="py-2 px-3">
                  <div className="mb-2"><small className="text-muted">Clics</small><div className="fw-bold">{formatNumberFull(kpis.clicks || 0)}</div></div>
                  <div className="mb-2"><small className="text-muted">Link Clics</small><div className="fw-bold">{formatNumberFull(kpis.linkClicks || 0)}</div></div>
                  <div className="mb-2"><small className="text-muted">CTR</small><div className="fw-bold">{formatPercent(kpis.ctr)}</div></div>
                  <div><small className="text-muted">Video Views</small><div className="fw-bold">{formatNumberFull(kpis.videoViews || 0)}</div></div>
                </Card.Body>
              </Card>
            </Col>
            {/* Costos */}
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #f5803e' }}>
                <Card.Header className="bg-light py-1 px-3 border-0"><small className="fw-bold text-uppercase" style={{ color: '#f5803e' }}>Costos</small></Card.Header>
                <Card.Body className="py-2 px-3">
                  <div className="mb-2"><small className="text-muted">Inversión Total</small><div className="fw-bold">{formatCurrency(kpis.cost || 0)}</div></div>
                  <div className="mb-2"><small className="text-muted">CPM</small><div className="fw-bold">{formatCurrency(kpis.cpm || 0)}</div></div>
                  <div className="mb-2"><small className="text-muted">CPC</small><div className="fw-bold">{formatCurrency(kpis.cpc || 0)}</div></div>
                  <div><small className="text-muted">CPC Enlace</small><div className="fw-bold">{formatCurrency(kpis.linkCpc || (kpis.linkClicks > 0 ? kpis.cost / kpis.linkClicks : 0))}</div></div>
                </Card.Body>
              </Card>
            </Col>
            {/* Conversiones */}
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #6b5eae' }}>
                <Card.Header className="bg-light py-1 px-3 border-0"><small className="fw-bold text-uppercase" style={{ color: '#6b5eae' }}>Conversiones</small></Card.Header>
                <Card.Body className="py-2 px-3">
                  <div className="mb-2"><small className="text-muted">Leads</small><div className="fw-bold">{formatNumberFull(kpis.leads || 0)}</div></div>
                  <div className="mb-2"><small className="text-muted">CPL</small><div className="fw-bold">{formatCurrency(kpis.cpl || 0)}</div></div>
                  <div className="mb-2"><small className="text-muted">WhatsApp</small><div className="fw-bold">{formatNumberFull(kpis.whatsappConversations || 0)}</div></div>
                  <div><small className="text-muted">Costo/WhatsApp</small><div className="fw-bold">{formatCurrency(kpis.costPerWhatsappConversation || 0)}</div></div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tabs */}
          <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
            <Card className="shadow-sm border-0 mb-3">
              <Card.Body className="py-0">
                <Nav variant="pills" className="nav-pills-falcon py-2">
                  {['overview', 'campaigns', 'demographics', 'platforms', 'geography'].map(key => (
                    <Nav.Item key={key}>
                      <Nav.Link eventKey={key}
                        className={`px-4 py-2 me-2 rounded-pill ${activeTab === key ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                        style={{ cursor: 'pointer', transition: 'all 0.2s' }}>
                        {key === 'overview' ? 'Overview' : key === 'campaigns' ? `Campañas ${sortedCampaigns?.length ? `(${sortedCampaigns.length})` : ''}` : key === 'demographics' ? 'Demografía' : key === 'platforms' ? 'Plataformas' : 'Geografía'}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </Card.Body>
            </Card>

            <Tab.Content>
              {/* ── OVERVIEW ── */}
              <Tab.Pane eventKey="overview">
                <Row className="g-3 mb-3"><Col lg={12}><EvolutiveChart data={data?.dailyTrend} /></Col></Row>

                <Row className="g-3 mb-3"><Col lg={12}><InvestmentByChannel campaigns={data?.campaigns} /></Col></Row>

                <Row className="g-3 mb-3"><Col lg={12}><AISignals kpis={kpis} campaigns={data?.campaigns} clientName="Tigo" dateRange={`${dateFrom.toISOString().slice(0, 10)} a ${dateTo.toISOString().slice(0, 10)}`} clientId={effectiveClientId} dateFrom={dateFrom} dateTo={dateTo} filters={{ platform: selectedPlatform.map(p => p.value), campaign: selectedCampaign.map(c => c.value), product: selectedProduct.map(p => p.value) }} /></Col></Row>

                <Row className="g-3 mb-3"><Col lg={12}><TopAdsSection ads={data?.ads} campaigns={data?.campaigns} /></Col></Row>

                <Row className="g-3 mb-3">
                  <Col lg={8}>
                    <Card className="shadow-sm border-0 h-100">
                      <Card.Header className="bg-white border-bottom py-2"><h6 className="mb-0">Tendencia General - Impresiones/Clicks/Gasto</h6></Card.Header>
                      <Card.Body><TrendChart data={data?.dailyTrend} /></Card.Body>
                    </Card>
                  </Col>
                  <Col lg={4}>
                    <Card className="shadow-sm border-0 h-100">
                      <Card.Header className="bg-white border-bottom py-2">
                        <h6 className="mb-0">Distribución por Edad</h6>
                        <small className="text-muted" style={{ fontSize: 10 }}>Solo Meta · ⓘ</small>
                      </Card.Header>
                      <Card.Body><AgeChart data={data?.byAge} /></Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* ── Google Analytics (inline, no tab separado) ── */}
                <Card className="shadow-sm border-0 mb-3 mt-3">
                  <Card.Header className="bg-white border-bottom py-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className="rounded-circle bg-success" style={{ width: 8, height: 8, animation: 'pulse 1.8s infinite' }}></span>
                      <h6 className="mb-0 fw-bold" style={{ color: '#34a853' }}>Google Analytics</h6>
                      <small className="text-muted">· datos de tráfico pago · web Movistar + web Tuenti</small>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-2 mb-3">
                      <Col md={4}><KPICard title="Sesiones totales" value={GA.sessions} subtitle="Tráfico pago UTM" color="#34a853" /></Col>
                      <Col md={4}><KPICard title="Duración promedio" value={GA.duration} subtitle={`Páginas/sesión: ${GA.pagesPerSession}`} color="#34a853" /></Col>
                      <Col md={4}><KPICard title="Tasa conversión" value={GA.convRate} subtitle="Sesión → evento" color="#e63757" /></Col>
                    </Row>
                    <Row className="g-2 mb-3">
                      <Col><KPICard title="Purchase" value={GA.purchases} subtitle="Pagos completados" color="#00d97e" /></Col>
                      <Col><KPICard title="Portabilidad" value={GA.portability} subtitle="Form submits" color="#2c7be5" /></Col>
                      <Col><KPICard title="Costo/Purchase" value={GA.costPerPurchase} subtitle="Inversión/purchase" color="#00d97e" /></Col>
                      <Col><KPICard title="Costo/Portabilidad" value={GA.costPerPortability} subtitle="Inversión/portabilidad" color="#f5803e" /></Col>
                    </Row>
                    <Card className="border">
                      <Card.Header className="py-2"><h6 className="mb-0 fw-semibold" style={{ fontSize: 13 }}>Embudo Ads → GA</h6></Card.Header>
                      <Card.Body>
                        <Row className="text-center">
                          {[
                            { label: 'Impresiones', value: '36.3M', pct: '100%', color: 'primary' },
                            { label: 'Alcance', value: '18.4M', pct: '50.7%', color: 'primary' },
                            { label: 'Sesiones GA', value: '142K', pct: '0.78%', color: 'info' },
                            { label: 'Eventos', value: '242', pct: '0.17%', color: 'warning' },
                            { label: 'Purchase', value: '148', pct: '61.2%', color: 'success' },
                          ].map((step, i) => (
                            <Col key={i}>
                              <div className={`fs-5 fw-bold text-${step.color}`}>{step.value}</div>
                              <small className="text-muted">{step.label}</small>
                              <div><Badge bg={step.color} className="mt-1">{step.pct}</Badge></div>
                            </Col>
                          ))}
                        </Row>
                        <p className="text-center text-muted small mt-2 mb-0">Mayor drop: Sesión → Evento (0.17%)</p>
                      </Card.Body>
                    </Card>
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* ── CAMPAIGNS ── */}
              <Tab.Pane eventKey="campaigns">
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Campañas · Ad Groups · Anuncios</h6>
                    <small className="text-muted">Clic en una fila para expandir</small>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <CampaignTreeTable
                      campaigns={sortedCampaigns}
                      adGroups={data?.adGroups || []}
                      ads={data?.ads || []}
                      sortBy={sortBy}
                      onSort={handleSort}
                      filters={filterCampaigns}
                      onFilterChange={setFilterCampaigns}
                    />
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* ── DEMOGRAPHICS ── */}
              <Tab.Pane eventKey="demographics">
                <Row className="g-3 mb-2">
                  <Col lg={12}>
                    <div className="alert alert-info py-2 mb-0" style={{ fontSize: 12 }}>
                      <strong>ⓘ Solo Meta:</strong> El desglose por edad solo está disponible para Meta Ads. TikTok y Google Ads no exponen estos breakdowns en su API.
                    </div>
                  </Col>
                </Row>
                <Row className="g-3">
                  <Col lg={6}>
                    <Card className="shadow-sm border-0">
                      <Card.Header className="bg-white border-bottom py-2"><h6 className="mb-0">Distribución por Edad <small className="text-muted">· solo Meta</small></h6></Card.Header>
                      <Card.Body>
                        <AgeChart data={data?.byAge} />
                        {data?.byAge && (() => {
                          const filteredAge = (data.byAge || []).filter(a => !filterDemographics.ageRange || (a.ageRange || '').toLowerCase().includes(filterDemographics.ageRange.toLowerCase()));
                          const sortedAge = [...filteredAge].sort((a, b) => { const aV = a[sortByDemographics.field] ?? 0, bV = b[sortByDemographics.field] ?? 0; return sortByDemographics.direction === 'asc' ? aV - bV : bV - aV; });
                          return (
                            <Table size="sm" className="mt-3" style={{ fontSize: '12px' }}>
                              <thead className="bg-light">
                                <tr>
                                  <th>Rango</th>
                                  <SortHeader field="impressions" label="Impresiones" sortBy={sortByDemographics} onSort={f => setSortByDemographics(p => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                                  <SortHeader field="clicks" label="Clicks" sortBy={sortByDemographics} onSort={f => setSortByDemographics(p => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                                  <SortHeader field="cost" label="Inversión" sortBy={sortByDemographics} onSort={f => setSortByDemographics(p => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                                </tr>
                                <tr className="bg-white border-bottom"><th style={{ padding: '4px 6px' }}><Form.Control size="sm" placeholder="Filtrar rango..." value={filterDemographics.ageRange || ''} onChange={e => setFilterDemographics(p => ({ ...p, ageRange: e.target.value }))} style={{ fontSize: '11px' }} /></th><th colSpan={2} className="text-muted small">{sortedAge.length} resultado(s)</th></tr>
                              </thead>
                              <tbody>
                                {sortedAge.map((a, i) => <tr key={i}><td>{a.ageRange}</td><td className="text-end">{formatNumber(a.impressions)}</td><td className="text-end">{formatNumber(a.clicks)}</td><td className="text-end">{formatCurrency(a.cost)}</td></tr>)}
                              </tbody>
                              <tfoot className="bg-light fw-bold">
                                <tr>
                                  <td>TOTAL</td>
                                  <td className="text-end">{formatNumber(sortedAge.reduce((s, a) => s + (a.impressions || 0), 0))}</td>
                                  <td className="text-end">{formatNumber(sortedAge.reduce((s, a) => s + (a.clicks || 0), 0))}</td>
                                  <td className="text-end">{formatCurrency(sortedAge.reduce((s, a) => s + (a.cost || 0), 0))}</td>
                                </tr>
                              </tfoot>
                            </Table>
                          );
                        })()}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={6}>
                    <Card className="shadow-sm border-0">
                      <Card.Header className="bg-white border-bottom py-2"><h6 className="mb-0">Top Regiones/Provincias</h6></Card.Header>
                      <Card.Body>
                        <LocationChart data={data?.byLocation} />
                        {data?.byLocation && (
                          <Table size="sm" className="mt-3" style={{ fontSize: '12px' }}>
                            <thead><tr><th>Región</th><th className="text-end">Impresiones</th><th className="text-end">Clicks</th><th className="text-end">Inversión</th></tr></thead>
                            <tbody>{data.byLocation.slice(0, 8).map((l, i) => <tr key={i}><td>{l.region}</td><td className="text-end">{formatNumber(l.impressions)}</td><td className="text-end">{formatNumber(l.clicks)}</td><td className="text-end">{formatCurrency(l.cost)}</td></tr>)}</tbody>
                            <tfoot className="bg-light fw-bold">
                              <tr>
                                <td>TOTAL (Top 8)</td>
                                <td className="text-end">{formatNumber(data.byLocation.slice(0, 8).reduce((s, l) => s + (l.impressions || 0), 0))}</td>
                                <td className="text-end">{formatNumber(data.byLocation.slice(0, 8).reduce((s, l) => s + (l.clicks || 0), 0))}</td>
                                <td className="text-end">{formatCurrency(data.byLocation.slice(0, 8).reduce((s, l) => s + (l.cost || 0), 0))}</td>
                              </tr>
                            </tfoot>
                          </Table>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab.Pane>

              {/* ── PLATFORMS ── */}
              <Tab.Pane eventKey="platforms">
                <Row className="g-3">
                  <Col lg={8}>
                    <Card className="shadow-sm border-0">
                      <Card.Header className="bg-white border-bottom py-2"><h6 className="mb-0">Rendimiento por Plataforma</h6></Card.Header>
                      <Card.Body><PlatformChart data={data?.byPlatform} /></Card.Body>
                    </Card>
                  </Col>
                  <Col lg={4}>
                    <Card className="shadow-sm border-0">
                      <Card.Header className="bg-white border-bottom py-2"><h6 className="mb-0">Detalle por Plataforma</h6></Card.Header>
                      <Card.Body className="p-0">
                        {data?.byPlatform && (() => {
                          const filteredPlat = (data.byPlatform || []).filter(p => !filterPlatforms.platform || (p.platform || '').toLowerCase().includes(filterPlatforms.platform.toLowerCase()));
                          const sortedPlat = [...filteredPlat].sort((a, b) => { const aV = a[sortByPlatforms.field] ?? 0, bV = b[sortByPlatforms.field] ?? 0; return sortByPlatforms.direction === 'asc' ? aV - bV : bV - aV; });
                          return (
                            <Table size="sm" className="mb-0" style={{ fontSize: '12px' }}>
                              <thead className="bg-light">
                                <tr>
                                  <th>Plataforma</th><th>Dispositivo</th>
                                  <SortHeader field="impressions" label="Impr." sortBy={sortByPlatforms} onSort={f => setSortByPlatforms(p => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                                  <SortHeader field="cost" label="Gasto" sortBy={sortByPlatforms} onSort={f => setSortByPlatforms(p => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                                </tr>
                                <tr className="bg-white border-bottom"><th style={{ padding: '4px 6px' }}><Form.Control size="sm" placeholder="Filtrar..." value={filterPlatforms.platform || ''} onChange={e => setFilterPlatforms(p => ({ ...p, platform: e.target.value }))} style={{ fontSize: '11px' }} /></th><th colSpan={2} className="text-muted small">{sortedPlat.length} resultado(s)</th></tr>
                              </thead>
                              <tbody>
                                {sortedPlat.map((p, i) => <tr key={i}><td><Badge bg={p.platform === 'facebook' ? 'primary' : p.platform === 'instagram' ? 'danger' : 'secondary'}>{p.platform}</Badge></td><td className="text-muted">{p.device}</td><td className="text-end">{formatNumber(p.impressions)}</td><td className="text-end">{formatCurrency(p.cost)}</td></tr>)}
                              </tbody>
                              <tfoot className="bg-light fw-bold">
                                <tr>
                                  <td>TOTAL</td><td></td>
                                  <td className="text-end">{formatNumber(sortedPlat.reduce((s, p) => s + (p.impressions || 0), 0))}</td>
                                  <td className="text-end">{formatCurrency(sortedPlat.reduce((s, p) => s + (p.cost || 0), 0))}</td>
                                </tr>
                              </tfoot>
                            </Table>
                          );
                        })()}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab.Pane>

              {/* ── GEOGRAPHY ── */}
              <Tab.Pane eventKey="geography">
                <Row className="g-3 mb-2">
                  <Col lg={12}>
                    <div className="alert alert-info py-2 mb-0" style={{ fontSize: 12 }}>
                      <strong>ⓘ Solo Meta:</strong> El desglose por región solo está disponible para Meta Ads.
                    </div>
                  </Col>
                </Row>
                <Row className="g-3">
                  <Col lg={8}>
                    <Card className="shadow-sm border-0 h-100">
                      <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Métricas por Región/Provincia <small className="text-muted">· solo Meta</small></h6>
                        <div className="btn-group btn-group-sm">
                          {[{ key: 'impressions', label: 'Impresiones' }, { key: 'clicks', label: 'Clicks' }, { key: 'cost', label: 'Inversión' }, { key: 'leads', label: 'Leads' }].map(m => (
                            <Button key={m.key} variant={geoMetric === m.key ? 'primary' : 'outline-secondary'} onClick={() => setGeoMetric(m.key)} size="sm">{m.label}</Button>
                          ))}
                        </div>
                      </Card.Header>
                      <Card.Body>
                        {data?.byLocation && (() => {
                          const sorted = [...data.byLocation].sort((a, b) => (b[geoMetric] || 0) - (a[geoMetric] || 0));
                          const maxV = sorted[0]?.[geoMetric] || 1;
                          return (
                            <Table hover responsive className="mb-0" style={{ fontSize: '12px' }}>
                              <thead className="bg-light">
                                <tr>
                                  <th style={{ width: '30%' }}>Región</th>
                                  <SortHeader field="impressions" label="Impresiones" sortBy={sortByGeo} onSort={f => setSortByGeo(p => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                                  <SortHeader field="clicks" label="Clicks" sortBy={sortByGeo} onSort={f => setSortByGeo(p => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                                  <th className="text-end">CTR</th>
                                  <SortHeader field="leads" label="Leads" sortBy={sortByGeo} onSort={f => setSortByGeo(p => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                                  <SortHeader field="cost" label="Inversión" sortBy={sortByGeo} onSort={f => setSortByGeo(p => ({ field: f, direction: p.field === f && p.direction === 'desc' ? 'asc' : 'desc' }))} />
                                </tr>
                                <tr className="bg-white border-bottom"><th style={{ padding: '4px 6px' }}><Form.Control size="sm" placeholder="Filtrar región..." value={filterGeo.region || ''} onChange={e => setFilterGeo(p => ({ ...p, region: e.target.value }))} style={{ fontSize: '11px' }} /></th><th colSpan={5} className="text-muted small">{sorted.length} resultado(s)</th></tr>
                              </thead>
                              <tbody>
                                {sorted.filter(l => !filterGeo.region || (l.region || '').toLowerCase().includes(filterGeo.region.toLowerCase())).map((l, i) => {
                                  const ctr = l.impressions > 0 ? (l.clicks / l.impressions * 100) : 0;
                                  return (
                                    <tr key={i}>
                                      <td><div className="d-flex align-items-center"><div className="me-2" style={{ width: `${Math.min(80, (l[geoMetric] / maxV) * 80)}px`, height: 6, backgroundColor: geoMetric === 'cost' ? '#e63757' : '#2c7be5', borderRadius: 3, minWidth: 3 }} /><span>{l.region || 'Desc.'}</span></div></td>
                                      <td className="text-end">{formatNumber(l.impressions)}</td><td className="text-end">{formatNumber(l.clicks)}</td>
                                      <td className="text-end"><Badge bg={ctr > 1 ? 'success' : ctr > 0.5 ? 'warning' : 'secondary'}>{formatPercent(ctr)}</Badge></td>
                                      <td className="text-end">{formatNumber(l.leads || 0)}</td><td className="text-end fw-bold">{formatCurrency(l.cost)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot className="bg-light fw-bold">
                                {(() => {
                                  const geoFiltered = sorted.filter(l => !filterGeo.region || (l.region || '').toLowerCase().includes(filterGeo.region.toLowerCase()));
                                  const totalImpr = geoFiltered.reduce((s, l) => s + (l.impressions || 0), 0);
                                  const totalClicks = geoFiltered.reduce((s, l) => s + (l.clicks || 0), 0);
                                  const totalLeads = geoFiltered.reduce((s, l) => s + (l.leads || 0), 0);
                                  const totalCost = geoFiltered.reduce((s, l) => s + (l.cost || 0), 0);
                                  return (
                                    <tr>
                                      <td>TOTAL ({geoFiltered.length} regiones)</td>
                                      <td className="text-end">{formatNumber(totalImpr)}</td>
                                      <td className="text-end">{formatNumber(totalClicks)}</td>
                                      <td className="text-end"><Badge bg={totalImpr > 0 && (totalClicks/totalImpr*100) > 1 ? 'success' : 'warning'}>{formatPercent(totalImpr > 0 ? totalClicks/totalImpr*100 : 0)}</Badge></td>
                                      <td className="text-end">{formatNumber(totalLeads)}</td>
                                      <td className="text-end">{formatCurrency(totalCost)}</td>
                                    </tr>
                                  );
                                })()}
                              </tfoot>
                            </Table>
                          );
                        })()}
                        {!data?.byLocation && <div className="text-center text-muted py-4">Sin datos geográficos</div>}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={4}>
                    <Card className="shadow-sm border-0 h-100">
                      <Card.Header className="bg-white border-bottom py-2"><h6 className="mb-0">Top 10 Regiones</h6></Card.Header>
                      <Card.Body className="p-0">
                        <Table hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
                          <thead className="bg-light"><tr><th>#</th><th>Región</th><th className="text-end">{geoMetric === 'impressions' ? 'Impr.' : geoMetric === 'clicks' ? 'Clicks' : geoMetric === 'cost' ? 'Inversión' : 'Leads'}</th></tr></thead>
                          <tbody>
                            {data?.byLocation?.sort((a, b) => (b[geoMetric] || 0) - (a[geoMetric] || 0)).slice(0, 10).map((l, i) => (
                              <tr key={i}><td><Badge bg="secondary" pill>{i + 1}</Badge></td><td className="text-truncate" style={{ maxWidth: 150 }}>{l.region || 'Desc.'}</td><td className="text-end fw-bold">{geoMetric === 'cost' ? formatCurrency(l[geoMetric]) : formatNumber(l[geoMetric] || 0)}</td></tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>

          <div className="text-center mt-3">
            <small className="text-muted">
              ⏱️ Procesado en {data?.meta?.processingTimeMs || 0}ms · {data?.summary?.totalCampaigns || 0} campañas · {data?.summary?.totalAdGroups || 0} ad groups · {data?.summary?.totalAds || 0} anuncios
            </small>
          </div>
        </>
      )}
    </Container>
  );
};

export default TigoDigitalDashboard;
